// src/services/axios.js
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

import router from '@/router';
import { logout } from '@/store/auth';
import i18n from "@/plugins/i18n";
import { getAccessToken, setAccessToken, migrateFromLocalStorageOnce } from '@/services/tokenStore';

// Создайте экземпляр Axios
const api = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || 'http://localhost:5001/api',
  withCredentials: true, // для передачи refresh cookie
});
const REFRESH_BEFORE_EXPIRY_MS = 60 * 1000;
const AUTH_PUBLIC_PATTERNS = [
  /\/auth\/refresh$/,
  /\/auth\/login$/,
  /\/auth\/register$/,
  /\/auth\/logout$/
];

let refreshPromise = null;
let refreshController = null;
let refreshGeneration = 0;

// Отменяем старое обновление при смене пользователя или выходе из аккаунта.
export function resetAccessTokenRefresh() {
  refreshGeneration += 1;
  if (refreshController) refreshController.abort();
  refreshController = null;
  refreshPromise = null;
}

function tokenNeedsRefresh(token) {
  try {
    const { exp } = jwtDecode(token);
    return typeof exp === 'number' && exp * 1000 - Date.now() <= REFRESH_BEFORE_EXPIRY_MS;
  } catch (_) {
    // Некорректный токен обрабатывается запасным путём через 401.
    return false;
  }
}

function endExpiredSession() {
  if (!getAccessToken()) return;
  logout();
  if (window.$toast) {
    window.$toast.show(i18n.global.t('authorizationRequired'), 'error');
  }
  router.push('/login');
}

// Bootstrap, проверка exp и обработка 401 используют один запрос на вкладку.
export function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;

  const generation = refreshGeneration;
  refreshController = new AbortController();
  refreshPromise = api.post('/auth/refresh', null, {
    skipAuthRedirect: true,
    skipAuthRefresh: true,
    signal: refreshController.signal,
  }).then((response) => {
    if (generation !== refreshGeneration) {
      throw new Error('Authentication session changed during refresh');
    }
    const token = response?.data?.access_token || response?.data?.data?.access_token;
    if (!token) throw new Error('No access token in refresh response');
    setAccessToken(token);
    return token;
  }).catch((error) => {
    if (generation === refreshGeneration && error.response?.status === 401) {
      endExpiredSession();
    }
    throw error;
  }).finally(() => {
    if (generation === refreshGeneration) {
      refreshPromise = null;
      refreshController = null;
    }
  });
  return refreshPromise;
}

// Интерцептор для обработки запросов
api.interceptors.request.use(
  async (config) => {
    config.headers['Accept-Language'] = i18n.global.locale.value;
    // Публичные маршруты (точные паттерны), где токен не обязателен
    const publicPatterns = [
      /\/neolink\/neolink_search_product/,
      /\/market_research\/\d+\/public$/, // публичное получение MR
      /\/market_research\/guest_answers$/, // гостевые ответы
      /\/download_author_file\//, // скачивание файлов автора (по проверкам на бэке)
      /\/download_position_document\//, // скачивание документов позиции для гостей
      /\/geo\/detect$/, // публичный автодетект страны/языка
      /\/auth\/guest\/send_code/, // отправка OTP для гостей
      /\/auth\/guest\/verify/, // верификация OTP для гостей
      /\/auth\/password_reset_request$/,
      /\/auth\/password_reset\//,
      /\/auth\/confirm\//,
      ...AUTH_PUBLIC_PATTERNS
    ];
    // Является ли URL публичным (по паттернам)
    const isPublicRoute = publicPatterns.some((re) => re.test(config.url));

    // Миграция с localStorage -> память (однократно на первом запросе)
    if (getAccessToken() == null) {
      migrateFromLocalStorageOnce();
    }
    // Получаем токен из памяти
    let token = getAccessToken();

    if (!isPublicRoute && !config.skipAuthRefresh && !config.skipAuthRedirect
        && !config._authRetry && (refreshPromise || (token && tokenNeedsRefresh(token)))) {
      token = await refreshAccessToken();
    }

    // Если токен существует и не является строкой "null", добавляем его
    if (token && token !== 'null' && token !== 'undefined') {
      // Добавляем токен в заголовки для НЕ ПУБЛИЧНЫХ маршрутов
      if (!isPublicRoute) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Интерцептор для обработки ответов
// ---------------------------------------------------------------------------
// AI-запросы в очереди (backend: services/ai_requests.py). Долгая AI-ручка
// отвечает 202 {ai_job_id}; перехватчик ждёт задачу и отдаёт её настоящий ответ,
// поэтому вызывающий код не меняется. Потоковые ручки (spec-чат) читают события
// через waitForAiJob(..., { onEvents }).
// ---------------------------------------------------------------------------
const AI_JOB_MAX_WAIT_MS = 20 * 60 * 1000;

// Файловые ручки зовут с responseType 'blob'/'arraybuffer' — тогда и 202
// {ai_job_id} приходит не объектом, а байтами JSON.
async function aiJobIdOf(response) {
  if (!response || response.status !== 202) return null;
  let payload = response.data;
  try {
    if (typeof Blob !== 'undefined' && payload instanceof Blob) {
      payload = JSON.parse(await payload.text());
    } else if (payload instanceof ArrayBuffer) {
      payload = JSON.parse(new TextDecoder().decode(payload));
    }
  } catch (e) {
    return null;
  }
  return payload?.data?.ai_job_id || payload?.ai_job_id || null;
}

function sleepOrAbort(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new axios.CanceledError());
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(new axios.CanceledError());
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener?.('abort', onAbort);
      resolve();
    }, ms);
    signal?.addEventListener?.('abort', onAbort, { once: true });
  });
}

export function cancelAiJob(jobId) {
  return api.post(`/ai-jobs/${jobId}/cancel`, null, { aiJobInternal: true }).catch(() => {});
}

/** Дождаться завершения AI-задачи. onEvents получает события потоковой ручки
 *  по порядку. При отмене через signal задача отменяется и на сервере. */
export async function waitForAiJob(jobId, { signal, onEvents } = {}) {
  const startedAt = Date.now();
  let after = 0;
  let delay = 500;
  try {
    for (;;) {
      await sleepOrAbort(delay, signal);
      const { data } = await api.get(`/ai-jobs/${jobId}`, {
        params: { after },
        signal,
        aiJobInternal: true,
      });
      if (data?.events?.length) {
        after = data.next;
        if (onEvents) await onEvents(data.events);
      }
      if (data?.done) return data;
      if (Date.now() - startedAt > AI_JOB_MAX_WAIT_MS) {
        throw new Error('AI request timed out');
      }
      delay = Math.min(delay + 250, 1500);
    }
  } catch (error) {
    if (axios.isCancel(error) || signal?.aborted) cancelAiJob(jobId);
    throw error;
  }
}

async function resolveAiJobResponse(jobId, config) {
  await waitForAiJob(jobId, { signal: config.signal });
  // Ответ ручки как есть (JSON или файл) — через обычные перехватчики.
  return api.get(`/ai-jobs/${jobId}/result`, {
    responseType: config.responseType,
    signal: config.signal,
    aiJobInternal: true,
  });
}

api.interceptors.response.use(
  async (response) => {
    const aiJobId = !response?.config?.aiJobInternal && !response?.config?.rawAiJob
      ? await aiJobIdOf(response)
      : null;
    if (aiJobId) {
      return resolveAiJobResponse(aiJobId, response.config);
    }
    // Упрощаем совместимость: если API вернул {success, data, message}, то пробрасываем внутренний data наружу
    try {
      const payload = response && response.data;
      if (payload && typeof payload === 'object') {
        const hasSuccess = Object.prototype.hasOwnProperty.call(payload, 'success');
        const hasData = Object.prototype.hasOwnProperty.call(payload, 'data');
        const topLevelKeys = Object.keys(payload || {});
        const onlyDataKey = topLevelKeys.length === 1 && hasData;

        // Не сплющиваем, если вместе с data есть другие ключи (например, import_info)
        if (hasSuccess || onlyDataKey) {
          const rawMsg = payload.message || payload.messages;
          let normalizedMessage = '';
          if (typeof rawMsg === 'string') normalizedMessage = rawMsg;
          else if (Array.isArray(rawMsg)) normalizedMessage = rawMsg.join('\n');
          else if (rawMsg && typeof rawMsg === 'object') normalizedMessage = Object.values(rawMsg).flat().join('\n');

          response._meta = {
            success: payload.success,
            message: normalizedMessage,
          };
          response.data = hasData ? payload.data : payload;

          // Если success === false, пробрасываем в catch, чтобы единообразно обрабатывать через messages
          if (hasSuccess && payload.success === false) {
            const error = new Error(normalizedMessage || 'Request failed');
            error._meta = response._meta;
            error.response = { ...response, data: payload };
            error.config = response.config;
            return Promise.reject(error);
          }
        }
      }
    } catch (_) {
      // no-op
    }
    return response;
  },
  (error) => {
    // Публичные маршруты (точные паттерны) — для response
    const publicPatterns = [
      /\/neolink\/neolink_search_product/,
      /\/market_research\/\d+$/, // публичное получение MR (без /public)
      /\/market_research\/\d+\/public$/, // публичное получение MR
      /\/market_research\/guest_answers$/, // гостевые ответы
      /\/download_author_file\//, // скачивание файлов автора
      /\/download_position_document\//, // скачивание документов позиции для гостей
      /\/geo\/detect$/, // публичный автодетект страны/языка
      /\/auth\/guest\/send_code/, // отправка OTP для гостей
      /\/auth\/guest\/verify/, // верификация OTP для гостей
      /\/auth\/password_reset_request$/,
      /\/auth\/password_reset\//,
      /\/auth\/confirm\//,
      ...AUTH_PUBLIC_PATTERNS
    ];
    // Проверяем, является ли URL запроса публичным маршрутом
    const isPublicRoute = error.config && publicPatterns.some((re) => re.test(error.config.url || ''));
    const hasToken = !!getAccessToken();

    // 429 приходит из двух источников: лимитер запросов (error_code =
    // 'rate_limited') и исчерпанные токены тарифа. Сообщения разные, поэтому
    // различаем их, иначе на форме входа всплывает «недостаточно токенов».
    if (error.response && error.response.status === 429) {
      const isRateLimited = error.response.data?.error_code === 'rate_limited';
      if (isRateLimited) {
        // Страницы авторизации показывают лимит прямо в форме.
        if (!error.config?.skipAuthRedirect) {
          import('@/utils/toast').then(({ showWarning }) => {
            showWarning(
              error.response.data?.message
                || i18n.global.t('password_reset_request.too_many'),
            );
          });
        }
      } else {
        import('@/utils/toast').then(({ showInsufficientTokens }) => {
          showInsufficientTokens();
        });
      }
      return Promise.reject(error);
    }

    if (error.config?.skipAuthRedirect || error.config?.skipAuthRefresh) {
      return Promise.reject(error);
    }

    if (error.response && error.response.status === 401 && !isPublicRoute) {
      // Если на странице market_research/:id и токена нет — не рефрешим и не уводим на логин
      const path = window.location && window.location.pathname ? window.location.pathname : '';
      const isMarketResearchPage = /^\/market_research\/\d+/.test(path);
      if (!hasToken && isMarketResearchPage) {
        return Promise.reject(error);
      }
      // Если токена нет вовсе — не пытаемся рефрешить и не редиректим с публичных страниц
      if (!hasToken) {
        return Promise.reject(error);
      }
      return handle401AndRetry(error);
    }
    return Promise.reject(error);
  }
);

async function handle401AndRetry(originalError) {
  const originalRequest = originalError.config;
  // Каждый запрос повторяется максимум один раз, включая ожидавшие refresh.
  if (originalRequest._authRetry) {
    return Promise.reject(originalError);
  }
  originalRequest._authRetry = true;

  // Запоздалый 401 мог прийти от запроса со старым, уже обновлённым токеном.
  const currentToken = getAccessToken();
  const sentAuthorization = originalRequest.headers?.get?.('Authorization')
    || originalRequest.headers?.Authorization;
  const token = currentToken && sentAuthorization !== `Bearer ${currentToken}`
    ? currentToken
    : await refreshAccessToken();
  originalRequest.headers['Authorization'] = `Bearer ${token}`;
  return api(originalRequest);
}

export default api;
